import { useState } from "react";
import type { AppSettings } from "@/lib/queries";
import { useUpdateAppSettings } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Webhooks & WhatsApp settings (2026-10-18 reorganization): the former
 * combined `WebhooksCard` was split into two sections rendered by the
 * Integrations tab's sub-tabs ("Webhooks" and "WhatsApp"). Pure
 * reorganization — no behavior change. Both sections are gated by the same
 * `settings.webhooks` canEdit the combined card used.
 *
 *  - WebhooksFlowsSection — ALL webhook FlowCards (referral reply, referral
 *    to, certificate, whatsappReminder — the reminder flow IS a webhook).
 *  - WhatsappRemindersSection — the WhatsApp Reminders card (whatsappEnabled
 *    switch + reminder lead + Save WhatsApp).
 */

interface FlowState {
  url: string;
  enabled: boolean;
  type: "pabbly" | "generic";
  template: string;
}

const FLOW_LABELS: Record<string, string> = {
  referralReply: "Referral Reply",
  referralTo: "Referral To",
  certificate: "Certificate",
  whatsappReminder: "WhatsApp Reminder",
};

const FIELD_MAP: Record<string, { url: keyof AppSettings; enabled: keyof AppSettings; type: keyof AppSettings; template: keyof AppSettings }> = {
  referralReply: { url: "referralReplyWebhookUrl", enabled: "referralReplyWebhookEnabled", type: "referralReplyWebhookType", template: "referralReplyTemplate" },
  referralTo: { url: "referralToWebhookUrl", enabled: "referralToWebhookEnabled", type: "referralToWebhookType", template: "referralToTemplate" },
  certificate: { url: "certificateWebhookUrl", enabled: "certificateWebhookEnabled", type: "certificateWebhookType", template: "certificateTemplate" },
  whatsappReminder: { url: "whatsappReminderWebhookUrl", enabled: "whatsappReminderWebhookEnabled", type: "whatsappReminderWebhookType", template: "whatsappTemplate" },
};

function FlowCard({ flow, settings, canEdit }: { flow: string; settings: AppSettings; canEdit: boolean }) {
  const fields = FIELD_MAP[flow];
  const initial: FlowState = {
    url: (settings[fields.url] as string) ?? "",
    enabled: settings[fields.enabled] as boolean,
    type: settings[fields.type] as "pabbly" | "generic",
    template: (settings[fields.template] as string) ?? "",
  };
  const isMasked = initial.url === "***";
  const [state, setState] = useState<FlowState>(initial);
  const update = useUpdateAppSettings();

  const save = async () => {
    try {
      await update.mutateAsync({
        [fields.url]: isMasked && state.url === "" ? "***" : state.url || null,
        [fields.enabled]: state.enabled,
        [fields.type]: state.type,
        [fields.template]: state.template || null,
      } as Record<string, unknown>);
    } catch { /* handled */ }
  };

  const reset = () => {
    setState({ url: "", enabled: state.enabled, type: state.type, template: "" });
    toast.info("Template cleared — save to store empty");
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-serif text-base text-maroon-700 dark:text-amber-500">{FLOW_LABELS[flow]}</CardTitle>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted">Enabled</Label>
          <Switch checked={state.enabled} onCheckedChange={(v) => setState({ ...state, enabled: v })} disabled={!canEdit} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Type</Label>
          <Select value={state.type} onValueChange={(v: "pabbly" | "generic") => setState({ ...state, type: v })} disabled={!canEdit}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pabbly">Pabbly</SelectItem>
              <SelectItem value="generic">Generic</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Webhook URL {isMasked && <span className="text-xs text-muted ml-2">(currently set — leave blank to keep)</span>}</Label>
          <Input value={state.url} disabled={!canEdit} onChange={(e) => setState({ ...state, url: e.target.value })} placeholder={isMasked ? "••••••••" : "https://..."} />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label>Payload Template</Label>
            <Button size="sm" variant="ghost" onClick={reset} disabled={!canEdit} className="h-6 text-xs"><RotateCcw size={12} className="mr-1" /> Reset</Button>
          </div>
          <Textarea value={state.template} disabled={!canEdit} onChange={(e) => setState({ ...state, template: e.target.value })} rows={4} placeholder='Use {{patientName}}, {{patientPhone}}, {{body}}, etc. Leave blank to send default JSON.' />
        </div>
        {canEdit && (
          <Button onClick={save} disabled={update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Save size={14} className="mr-1" /> Save
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/** Integrations → Webhooks sub-tab: every webhook flow (incl. the WhatsApp
 *  Reminder webhook flow — that IS a webhook). Same grid the combined card
 *  used. */
export function WebhooksFlowsSection({ settings, canEdit }: { settings: AppSettings; canEdit: boolean }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <FlowCard flow="referralReply" settings={settings} canEdit={canEdit} />
      <FlowCard flow="referralTo" settings={settings} canEdit={canEdit} />
      <FlowCard flow="certificate" settings={settings} canEdit={canEdit} />
      <FlowCard flow="whatsappReminder" settings={settings} canEdit={canEdit} />
    </div>
  );
}

/** Integrations → WhatsApp sub-tab: the WhatsApp Reminders card (enable
 *  switch + reminder lead + Save WhatsApp), unchanged from the combined
 *  card's inner card. */
export function WhatsappRemindersSection({ settings, canEdit }: { settings: AppSettings; canEdit: boolean }) {
  const [whatsappEnabled, setWhatsappEnabled] = useState(settings.whatsappEnabled);
  const [reminderLead, setReminderLead] = useState(String(settings.reminderLeadMinutes ?? 120));
  const update = useUpdateAppSettings();

  const saveWhatsapp = async () => {
    try {
      await update.mutateAsync({ whatsappEnabled, reminderLeadMinutes: parseInt(reminderLead, 10) || 120 });
    } catch { /* handled */ }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="font-serif text-base">WhatsApp Reminders</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Switch checked={whatsappEnabled} onCheckedChange={setWhatsappEnabled} disabled={!canEdit} />
          <Label>Enable WhatsApp reminders</Label>
        </div>
        <div>
          <Label>Reminder Lead (minutes)</Label>
          <Input type="number" value={reminderLead} disabled={!canEdit} onChange={(e) => setReminderLead(e.target.value)} />
        </div>
        <p className="text-xs text-muted">DND hours: 21:00–09:00 IST enforced by the reminder cron (built in a later task).</p>
        {canEdit && (
          <Button onClick={saveWhatsapp} disabled={update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Save size={14} className="mr-1" /> Save WhatsApp
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
